import { useRescheduleInterviewMutation } from "@/app/features/recruitment/interview/interview.api";
import type { CustomModalProps } from "@/components/CustomModal";
import { Calendar03Icon } from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import {
  DATE_FORMATS,
  disablePastTimes,
  formatDate,
  formatTime,
} from "@/utils";
import { Button, DatePicker, Flex, Form, Modal } from "antd";

type Props = CustomModalProps & { id?: string };

export default function RescheduleInterview({ open, handleClose, id }: Props) {
  const [form] = Form.useForm();

  const [rescheduleInterview, { isLoading }] = useRescheduleInterviewMutation();

  const handleSubmit = async (values: any) => {
    if (!id) return;
    const payload = {
      interview_date: formatDate(values?.interview_date, DATE_FORMATS.ISO_DATE),
      interview_time: formatTime(values?.interview_time, "TIME_24H"),
    };

    try {
      const res = await rescheduleInterview({ id, body: payload }).unwrap();
      if (res?.success === false) {
        toast("error", getErrorMessage(res, "Failed to reschedule interview"));
        return;
      }
      toast(
        "success",
        getErrorMessage(res, "Interview rescheduled successfully"),
      );
      form.resetFields();
      handleClose();
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to reschedule interview"));
    }
  };

  return (
    <Modal
      centered
      width={420}
      open={open}
      footer={null}
      closable
      className="custom-modal"
      rootClassName="custom-bg-modal"
      title={
        <span className="text-white text-lg font-semibold">
          Reschedule Interview
        </span>
      }
      onCancel={() => {
        handleClose();
        form.resetFields();
      }}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form mt-4 space-y-2"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="New Interview Date"
          name="interview_date"
          rules={[{ required: true, message: "Please select interview date" }]}
        >
          <DatePicker
            picker="date"
            format="DD MMM, YYYY"
            placeholder="DD MMM, YYYY"
            style={{ width: "100%" }}
            className="transparent-picker"
            suffixIcon={<Calendar03Icon size={18} className="text-white" />}
            disabledDate={(current) =>
              current && current.isBefore(new Date(), "day")
            }
          />
        </Form.Item>

        <Form.Item
          label="New Interview Time"
          name="interview_time"
          rules={[{ required: true, message: "Please select interview time" }]}
        >
          <DatePicker
            picker="time"
            format="hh:mm A"
            placeholder="HH:MM"
            showNow={false}
            needConfirm={false}
            style={{ width: "100%" }}
            className="transparent-picker"
            disabledTime={disablePastTimes}
          />
        </Form.Item>

        <Flex justify="end" gap={12} className="mt-6">
          <Button
            htmlType="button"
            className="min-w-[100px] h-10 !border-teal-500 !text-teal-500 !bg-transparent !font-semibold"
            onClick={() => {
              handleClose();
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            htmlType="submit"
            loading={isLoading}
            className="btn-primary min-w-[100px] h-10 !font-semibold"
          >
            Reschedule
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}
